import { z } from 'zod';

/**
 * Perfil de política de viagem do viajante, como a op `getTravelerPolicyProfile`
 * (voll-apollo) o devolve: só o que a policy-api informou (sem `null`) e sem
 * `hasAccess` (seção sem acesso simplesmente não vem). Todo campo é opcional.
 */
const place = z.object({ code: z.string(), name: z.string() }).partial().strict();
const customPolicy = z
  .object({ description: z.string(), fullyCustomized: z.boolean() })
  .partial()
  .strict();
const flightException = z
  .object({
    classes: z.array(z.string().nullish()),
    origin: place,
    destination: place,
    allowed: z.boolean(),
    limits: z
      .object({ type: z.string(), oneWay: z.number(), twoWay: z.number() })
      .partial()
      .strict(),
  })
  .partial()
  .strict();
const codeNameEnabled = z
  .array(z.object({ code: z.string(), name: z.string(), enabled: z.boolean() }).partial().strict())
  .optional();
const window = {
  bookingWindow: z.number().optional(),
  bookingWindowInternational: z.number().optional(),
};
const str = z.string().optional();
const bool = z.boolean().optional();
const num = z.number().optional();

const FlightSchema = z
  .object({
    ...window,
    allowedClasses: z
      .array(
        z
          .object({ class: z.string(), valueLimit: z.number(), durationLimit: z.number() })
          .partial()
          .strict(),
      )
      .optional(),
    classLimits: z
      .array(
        z
          .object({
            class: z.string(),
            limits: z
              .object({ type: z.string(), oneWay: z.number(), twoWay: z.number() })
              .partial()
              .strict(),
          })
          .partial()
          .strict(),
      )
      .optional(),
    countryExceptions: z.array(flightException).optional(),
    airportExceptions: z.array(flightException).optional(),
    cheapestPrice: bool,
    cheapestPriceFastest: bool,
    cheapestPriceNonStop: bool,
    cheapestPricePeriod: bool,
    baggageOnlyOnMultipleSegments: bool,
    baggageOnlyAboveTripLength: bool,
    baggageOnlyAboveTripLengthValue: num,
    voucherAllowBaggage: bool,
    insurance: bool,
    comfortSeat: bool,
    info: str,
    infoLink: str,
    customPolicy: customPolicy.optional(),
  })
  .strict();

const HotelSchema = z
  .object({
    ...window,
    maxPrice: num,
    maxPriceInternational: num,
    maxPricePerCity: z
      .array(z.object({ city: z.string(), price: z.number() }).partial().strict())
      .optional(),
    maxPricePerCountry: z
      .array(z.object({ code: z.string(), name: z.string(), price: z.number() }).partial().strict())
      .optional(),
    cheapestPrice: bool,
    cheapestHotel: bool,
    onlyRateWithBreakfast: bool,
    onlyRateRefundable: bool,
    minRating: num,
    currencySymbol: str,
    info: str,
    infoLink: str,
    customPolicy: customPolicy.optional(),
  })
  .strict();

const RentalSchema = z
  .object({
    ...window,
    categories: codeNameEnabled,
    dropOffDistinct: bool,
    durationExceeding: bool,
    info: str,
    infoLink: str,
  })
  .strict();

const BusSchema = z
  .object({
    ...window,
    classes: codeNameEnabled,
    info: str,
    infoLink: str,
    customPolicy: customPolicy.optional(),
  })
  .strict();

export const TravelerPolicyProfileSchema = z
  .object({
    name: str,
    flight: FlightSchema.optional(),
    hotel: HotelSchema.optional(),
    rental: RentalSchema.optional(),
    bus: BusSchema.optional(),
  })
  .strict();
export type TravelerPolicyProfileWire = z.infer<typeof TravelerPolicyProfileSchema>;

/**
 * Resposta do customer-api com o perfil: `ok` carrega o perfil; `unresolved` =
 * viajante não resolvido (ou de outro cliente); `unavailable` = policy-api fora.
 */
export const TravelPolicyResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), profile: TravelerPolicyProfileSchema }),
  z.object({ status: z.literal('unresolved') }),
  z.object({ status: z.literal('unavailable') }),
]);
export type TravelPolicyResponse = z.infer<typeof TravelPolicyResponseSchema>;
